/**
 * Sistema de lemas mensuales.
 *
 * Ahora los lemas se gestionan desde el CMS (tabla monthly_mottos en el backend).
 * Este archivo mantiene también un mapa hardcodeado (fallback) que se usa
 * cuando la app no ha podido consultar el backend o cuando no hay lema
 * configurado para el mes/año actual.
 */

const isNative = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();
const API_BASE_URL = isNative
  ? 'https://api.appagustinos.es/api'
  : (process.env.REACT_APP_API_URL || 'https://api.appagustinos.es/api');

const CACHE_KEY_PREFIX = 'agustinos_motto';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 horas

// Fallback hardcodeado (se usa cuando no hay lema en el CMS o falla la red).
const FALLBACK_MOTTOS = {
  0: 'Somos Comunidad que trabaja por la paz',   // Enero
  1: 'Somos Comunidad Solidaria',                // Febrero
  2: 'Somos Comunidad Renovada y Renovadora',    // Marzo
  3: 'Somos Comunidad Resucitada',               // Abril
  4: 'Somos Comunidad Mariana',                  // Mayo
  5: 'Somos Comunidad Agradecida',               // Junio
  6: 'Somos Comunidad Festiva',                  // Julio
  7: 'Somos Comunidad que es Familia',           // Agosto
  8: 'Somos Comunidad Acogedora',                // Septiembre
  9: 'Somos Comunidad Misionera',                // Octubre
  10: 'Somos Comunidad Fraterna',                // Noviembre
  11: 'Somos Comunidad Esperanzada'              // Diciembre
};

// Devuelve inmediatamente el lema hardcodeado del mes actual.
// Útil como valor inicial mientras se resuelve el fetch al backend.
export const getMonthlyMotto = () => {
  const month = new Date().getMonth(); // 0-11
  return FALLBACK_MOTTOS[month] || 'Somos Comunidad Misionera';
};

// Consulta al backend el lema del mes/año actual.
// Estrategia network-first: siempre intenta preguntar al backend (para que
// los cambios en el CMS se reflejen al momento). El cache en localStorage se
// usa únicamente como respaldo cuando la red falla (modo avión, servidor caído).
// Devuelve el texto del lema, o null si no hay lema configurado / no hay red.
// Nunca lanza: si algo falla y no hay cache, resuelve a null y el caller usa el fallback.
export const fetchCurrentMotto = async () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1..12
  const cacheKey = `${CACHE_KEY_PREFIX}_${year}_${month}`;

  // 1) Fetch al backend (fresh siempre que sea posible).
  try {
    const res = await fetch(`${API_BASE_URL}/mottos/current`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (res.ok) {
      const body = await res.json();
      const text = body?.data?.text || null;

      // Guardo en cache para modo offline.
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(cacheKey, JSON.stringify({ text, at: Date.now() }));
        }
      } catch { /* ignore */ }

      return text;
    }
  } catch {
    // Sin red: cae al cache.
  }

  // 2) Cache como respaldo (respetando TTL para no servir algo muy viejo).
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(cacheKey) : null;
    if (raw) {
      const cached = JSON.parse(raw);
      if (cached && (Date.now() - cached.at) < CACHE_TTL_MS) {
        return cached.text || null;
      }
    }
  } catch { /* ignore */ }

  return null;
};

// Para debugging o uso en otros componentes (mantiene compatibilidad).
export const getAllMottos = () => ({ ...FALLBACK_MOTTOS });
